import { ApiError } from '@/lib/errors';
import { sanitizeError } from '@/lib/utils/errors';

/**
 * Error hierarchy for property type operations.
 *
 * Provides typed errors with correlation IDs for debugging and tracing.
 *
 * @module
 */

/**
 * Base error class for property type operations.
 */
export class PropertyTypeError extends Error {
  public failed_batch_index?: number;

  constructor(
    message: string,
    public code: string,
    public correlationId?: string,
    public statusCode?: number,
    failedBatchIndex?: number
  ) {
    super(message);
    this.name = 'PropertyTypeError';
    this.failed_batch_index = failedBatchIndex;
    // Ensure proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, PropertyTypeError.prototype);
  }
}

/**
 * Error for validation failures (400 errors).
 */
export class PropertyTypeValidationError extends PropertyTypeError {
  constructor(message: string, correlationId?: string, statusCode?: number) {
    super(message, 'VALIDATION_ERROR', correlationId, statusCode);
    this.name = 'PropertyTypeValidationError';
    Object.setPrototypeOf(this, PropertyTypeValidationError.prototype);
  }
}

/**
 * Error for permission/authorization failures (403 errors).
 */
export class PropertyTypePermissionError extends PropertyTypeError {
  constructor(message: string, correlationId?: string, statusCode?: number) {
    super(message, 'PERMISSION_ERROR', correlationId, statusCode);
    this.name = 'PropertyTypePermissionError';
    Object.setPrototypeOf(this, PropertyTypePermissionError.prototype);
  }
}

/**
 * Error for sequence collision / conflict errors (409 errors).
 *
 * Indicates a concurrent write to the same entity. Contains structured
 * detail from the server to support client-side retry with fresh seq.
 */
export class PropertyTypeConflictError extends PropertyTypeError {
  public expectedSeq?: number;
  public lastSeq?: number;
  public entityId?: string;
  /** Who made the conflicting change, if available. */
  public lastChangedBy?: {
    name: string;
    source_type: string;
    source_id?: string;
  };

  constructor(
    message: string,
    detail?: {
      expected_seq?: number;
      last_seq?: number;
      entity_id?: string;
      last_changed_by?: {
        name: string;
        source_type: string;
        source_id?: string;
      };
    },
    correlationId?: string,
    statusCode?: number
  ) {
    super(message, 'SEQUENCE_COLLISION', correlationId, statusCode);
    this.name = 'PropertyTypeConflictError';
    this.expectedSeq = detail?.expected_seq;
    this.lastSeq = detail?.last_seq;
    this.entityId = detail?.entity_id;
    this.lastChangedBy = detail?.last_changed_by;
    Object.setPrototypeOf(this, PropertyTypeConflictError.prototype);
  }
}

/** Exported for use in rendering conditionals that need to detect permission errors by message. */
export const PERMISSION_DENIED_MESSAGE =
  "You don't have permission to make this change.";

/**
 * User-friendly messages for known CRM event error types.
 * Maps backend error_type codes to display-safe strings.
 */
const EVENT_ERROR_MESSAGES: Record<string, string> = {
  unique_constraint_violation:
    'This value is already in use. Update the existing record or use a different value.',
  merged_object_modification:
    'This record has been merged into another record. Please update the merged record instead.',
  deleted_object_operation:
    "This record has been deleted and can't be modified.",
  entity_not_found: 'This record no longer exists.',
  association_cardinality_violation:
    'This record has reached its limit for this type of association. Remove an existing link before adding a new one.',
  permission_denied: PERMISSION_DENIED_MESSAGE,
  sequence_collision:
    'Someone else just made a change. Please review and try again.',
  internal_error: 'Something went wrong on our end. Please try again later.',
  projection_error:
    'Something went wrong. Try again, and get in touch if it keeps happening.',
  invalid_property_type:
    "This field isn't available. Please refresh the page and try again.",
  entity_type_mismatch: "This action isn't supported for this type of record.",
  invalid_schema_reference:
    'A referenced schema definition no longer exists. Please refresh the page and try again.',
  invalid_association_type:
    "This association type isn't available. Please refresh the page and try again.",
  invalid_pipeline_stage:
    "This pipeline stage isn't available. Please refresh the page and try again.",
  association_immutable: "This link can't be changed after it's created.",
};

const PASS_THROUGH_ERROR_TYPES = new Set([
  'validation_error',
  'required_property_violation',
  'validation_rule_violation',
]);

const DEFAULT_EVENT_ERROR_MESSAGE =
  'Something went wrong. Try again, and get in touch if it keeps happening.';

/**
 * Translate an error_type code and raw message into a user-friendly string.
 *
 * Used by EventsClient.wrapError (which has raw strings from the API response)
 * and by getUserFriendlyEventError (which extracts from error objects).
 */
export function translateEventError(
  errorType: string | undefined,
  rawMessage: string
): string {
  if (errorType && PASS_THROUGH_ERROR_TYPES.has(errorType)) {
    return sanitizeError(rawMessage);
  }
  if (errorType) {
    const mapped = EVENT_ERROR_MESSAGES[errorType];
    if (mapped) {
      return mapped;
    }
  }
  return DEFAULT_EVENT_ERROR_MESSAGE;
}

/**
 * User-friendly messages for bulk-delete error types. Contact-specific copy.
 * Falls through to a generic "Failed to delete" message for unknown types.
 */
const BULK_DELETE_ERROR_MESSAGES: Record<string, string> = {
  permission_denied: "You don't have permission to delete these records.",
  sequence_collision:
    'Some records changed while deleting. Refresh and try again.',
  selection_too_large:
    'Selection is too large. Narrow your filters and try again.',
};

const DEFAULT_BULK_DELETE_ERROR_MESSAGE =
  "We couldn't delete these records. Try again.";

/**
 * Translate a bulk-delete error_type to user-facing copy.
 *
 * Never returns the raw server message — falls through to a generic
 * "Failed to delete" string so we never leak internal validation strings
 * (e.g. "Batch size exceeds maximum of 100 events") to the user.
 */
export function translateBulkDeleteError(
  errorType: string | undefined
): string {
  if (errorType) {
    const mapped = BULK_DELETE_ERROR_MESSAGES[errorType];
    if (mapped) {
      return mapped;
    }
  }
  return DEFAULT_BULK_DELETE_ERROR_MESSAGE;
}

export function translateBulkChangeStageError(
  errorType: string | undefined
): string {
  switch (errorType) {
    case 'permission_denied':
      return "You don't have permission to change stages for these records.";
    case 'sequence_collision':
      return 'These records were updated while we were processing. Please try again.';
    case 'selection_too_large':
      return 'Selection is too large. Narrow your filters and try again.';
    case 'validation_error':
      return 'Invalid request. Please refresh and try again.';
    case 'cross_pipeline':
      return "Selected deals span multiple pipelines. Use 'Move to pipeline' instead.";
    default:
      return "We couldn't change stages. Try again.";
  }
}

export function translateBulkEnrichError(
  errorType: string | undefined
): string {
  switch (errorType) {
    case 'permission_denied':
      return "You don't have permission to enrich these contacts.";
    case 'sequence_collision':
      return 'These contacts were updated while we were processing. Please try again.';
    case 'selection_too_large':
      return 'Selection is too large. Narrow your filters and try again.';
    case 'validation_error':
      return 'Invalid request. Please refresh and try again.';
    default:
      return "We couldn't enrich these contacts. Try again.";
  }
}

export function translateBulkUpdateError(
  errorType: string | undefined
): string {
  switch (errorType) {
    case 'permission_denied':
      return "You don't have permission to update these records.";
    case 'sequence_collision':
      return 'These records were updated while we were processing. Please try again.';
    case 'selection_too_large':
      return 'Selection is too large. Narrow your filters and try again.';
    case 'validation_error':
      return 'Invalid value. Please check your input and try again.';
    default:
      return "We couldn't update these records. Try again.";
  }
}

/**
 * Get a user-friendly error message for any CRM event error.
 *
 * Handles:
 * - PropertyTypeError instances (reads .code) — the shape EventsClient.wrapError
 *   produces.
 * - Raw ApiError instances — the shape apiCall throws directly. Some submit
 *   paths (e.g. the deprecated CRMObjectClient.submitEvents, used by the deal
 *   stage-move flow) only wrap 409 conflicts and rethrow every other status
 *   unwrapped. The structured `{error_type, error}` still lives on
 *   `apiError.response`, so translate from there rather than discarding it.
 * - Anything else — generic fallback.
 *
 * Can be called from any catch block.
 */
export function getUserFriendlyEventError(error: unknown): string {
  if (error instanceof PropertyTypeError) {
    // Normalize constructor defaults (e.g. 'PERMISSION_ERROR') to backend
    // error_type codes (lowercase, snake_case).  wrapError already mutates
    // .code to the backend value, but errors from CRMObjectClient or direct
    // construction keep the uppercase defaults.
    const normalizedCode =
      error.code.toLowerCase() === 'permission_error'
        ? 'permission_denied'
        : error.code.toLowerCase();
    return translateEventError(normalizedCode, error.message);
  }
  if (error instanceof ApiError) {
    const response = error.response as
      | { error_type?: string; error?: string; detail?: string }
      | undefined;
    // The submit endpoint returns the user-facing string under `error`; `detail`
    // is a structured object (not a string) on rule violations, so it is only a
    // fallback when it happens to be a string. `error.message` backstops both.
    const rawMessage =
      (typeof response?.error === 'string' && response.error) ||
      (typeof response?.detail === 'string' && response.detail) ||
      error.message;
    return translateEventError(response?.error_type, rawMessage);
  }
  return DEFAULT_EVENT_ERROR_MESSAGE;
}
